import { Head, Link, usePage } from "@inertiajs/react"
import { ChevronRight } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { AdminPageHeader, EmptyState } from "@/components/admin/AdminPageHeader"
import { PipelineBadge, StatusBadge } from "@/components/admin/Badges"
import { FilterBar } from "@/components/admin/FilterBar"
import { enquiryTitle, plural } from "@/components/admin/format"
import { CapNote, ResponsiveList } from "@/components/admin/ResponsiveList"
import { formatDateOnly, formatDay } from "@/lib/dates"
import { formatPenceOr } from "@/lib/money"
import type { AdminEnquiriesIndexProps, AdminEnquirySummary, PageProps } from "@/types/inertia"

const TITLE = "Enquiries"
const DESCRIPTION =
  "Every order and consultation request, newest first, filtered by status, type and whether it was won or lost."

export default function AdminEnquiriesIndex() {
  const { props } = usePage<PageProps<AdminEnquiriesIndexProps>>()
  const { enquiries, total, limit, filters, options } = props
  const filtered = Boolean(filters.status || filters.enquiry_type || filters.pipeline)

  return (
    <>
      <Head title={TITLE}>
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AdminShell>
        <AdminPageHeader
          title="Enquiries"
          description="Orders and consultation requests, newest first. Open one to update its status, mark it won or lost, or keep notes."
        />

        <FilterBar
          path="/admin/enquiries"
          idPrefix="enquiries-filter"
          values={filters}
          fields={[
            { kind: "select", name: "status", label: "Status", options: options.statuses, allLabel: "Any status" },
            { kind: "select", name: "enquiry_type", label: "Type", options: options.enquiry_types, allLabel: "Orders and consultations" },
            { kind: "select", name: "pipeline", label: "Won / lost", options: options.pipelines, allLabel: "Open, won or lost" },
          ]}
        />

        <p className="mt-6" aria-live="polite">
          {plural(total, "enquiry", "enquiries")}
          {filtered ? (total === 1 ? " matches." : " match.") : "."}
        </p>
        <CapNote limit={limit} total={total} noun="enquiries" />

        <div className="mt-3">
          {enquiries.length === 0 ? (
            <EmptyState>
              {filtered ? "No enquiries match these filters." : "No enquiries yet. Orders and consultation requests from the site land here."}
            </EmptyState>
          ) : (
            <ResponsiveList
              label="Enquiries"
              items={enquiries}
              getKey={(enquiry) => enquiry.id}
              card={(enquiry) => <EnquiryCard enquiry={enquiry} />}
              columns={[
                {
                  key: "enquiry",
                  header: "Enquiry",
                  cell: (enquiry) => (
                    <div className="min-w-0">
                      <div className="font-medium">
                        <Link href={enquiry.url}>{enquiryTitle(enquiry)}</Link>
                      </div>
                      <div className="text-xs text-ink-muted">
                        {enquiry.name}
                        {enquiry.company && ` · ${enquiry.company}`}
                      </div>
                    </div>
                  ),
                },
                {
                  key: "event",
                  header: "Event",
                  cell: (enquiry) => (
                    <div>
                      <div>{enquiry.event_type_label}</div>
                      <div className="text-xs text-ink-muted">{eventMeta(enquiry)}</div>
                    </div>
                  ),
                },
                {
                  key: "status",
                  header: "Status",
                  cell: (enquiry) => <StatusBadge status={enquiry.status} label={enquiry.status_label} />,
                },
                {
                  key: "pipeline",
                  header: "Won / lost",
                  cell: (enquiry) => <PipelineBadge pipeline={enquiry.pipeline} label={enquiry.pipeline_label} />,
                },
                {
                  key: "received",
                  header: "Received",
                  className: "whitespace-nowrap",
                  cell: (enquiry) => formatDay(enquiry.created_at),
                },
              ]}
            />
          )}
        </div>
      </AdminShell>
    </>
  )
}

/** "12 Jun 2027 · 120 bottles · £1,638.00", whichever parts are known. */
function eventMeta(enquiry: AdminEnquirySummary) {
  const parts: string[] = []
  if (enquiry.event_date) parts.push(formatDateOnly(enquiry.event_date))
  if (enquiry.bottle_count !== null) {
    parts.push(`${enquiry.enquiry_type === "consultation" ? "About " : ""}${plural(enquiry.bottle_count, "bottle")}`)
  }
  if (enquiry.estimate_pence !== null) parts.push(formatPenceOr(enquiry.estimate_pence))
  return parts.join(" · ") || "No date or bottles yet"
}

function EnquiryCard({ enquiry }: { enquiry: AdminEnquirySummary }) {
  return (
    <Link href={enquiry.url} className="flex items-center gap-3 px-4 py-3 no-underline hover:bg-surface">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3">
          <span className="text-sm font-medium text-ink-display">{enquiryTitle(enquiry)}</span>
          <span className="text-xs text-ink-muted">{formatDay(enquiry.created_at)}</span>
        </div>
        <div className="truncate text-sm text-ink-body">
          {enquiry.name}
          {enquiry.company && ` · ${enquiry.company}`}
        </div>
        <div className="text-xs text-ink-muted">
          {enquiry.event_type_label} · {eventMeta(enquiry)}
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <StatusBadge status={enquiry.status} label={enquiry.status_label} />
          <PipelineBadge pipeline={enquiry.pipeline} label={enquiry.pipeline_label} />
        </div>
      </div>
      <ChevronRight className="h-4 w-4 shrink-0 text-ink-muted" aria-hidden="true" />
    </Link>
  )
}
